import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { SearchResult, SearchQuery, PaginatedResponse, ApiError } from '../../types';
import { RootState } from '@store/index';

interface SearchState {
  results: SearchResult[];
  query: string;
  total: number;
  page: number;
  pageSize: number;
  facets: Record<string, Array<{ value: string; count: number }>>;
  recentSearches: string[];
  isLoading: boolean;
  error: string | null;
}

const initialState: SearchState = {
  results: [],
  query: '',
  total: 0,
  page: 1,
  pageSize: 20,
  facets: {},
  recentSearches: [],
  isLoading: false,
  error: null,
};

/** Recorded when a rejection names no code; resolves to `search.searchFailed`. */
export const SEARCH_FAILED = 'SEARCH_FAILED';

const started = (state: SearchState) => {
  state.isLoading = true;
  state.error = null;
};

// The code is the contract; the server's prose is a log artifact and must
// not become UI text, so it never enters state.
const failed = (state: SearchState, code: string | undefined) => {
  state.isLoading = false;
  state.error = code || SEARCH_FAILED;
};

// Async thunks for search operations
export const performSearch = createAsyncThunk<
  PaginatedResponse<SearchResult> & {
    facets: Record<string, Array<{ value: string; count: number }>>;
    searchTime?: number;
  },
  SearchQuery,
  { rejectValue: ApiError }
>('search/performSearch', async (searchQuery, { rejectWithValue }) => {
  try {
    // Import api here to avoid circular dependency
    const { api } = await import('../../services/api');

    const response = await api.post<PaginatedResponse<SearchResult> & {
      facets: Record<string, Array<{ value: string; count: number }>>;
      searchTime?: number;
    }>('/search', searchQuery);

    return response;
  } catch (error: any) {
    return rejectWithValue(error);
  }
});

export const getSearchSuggestions = createAsyncThunk<
  { suggestions: Array<{ text: string; type: string; score: number }> },
  { query: string; limit?: number },
  { rejectValue: ApiError }
>('search/getSearchSuggestions', async ({ query, limit = 10 }, { rejectWithValue }) => {
  try {
    const { api } = await import('../../services/api');

    const response = await api.get<{ suggestions: Array<{ text: string; type: string; score: number }> }>(
      `/search/suggestions?q=${encodeURIComponent(query)}&limit=${limit}`
    );

    return response;
  } catch (error: any) {
    return rejectWithValue(error);
  }
});

// Slice
const searchSlice = createSlice({
  name: 'search',
  initialState,
  reducers: {
    setQuery: (state, action: PayloadAction<string>) => {
      state.query = action.payload;
    },
    addRecentSearch: (state, action: PayloadAction<string>) => {
      const search = action.payload.trim();
      if (search && !state.recentSearches.includes(search)) {
        state.recentSearches = [search, ...state.recentSearches.slice(0, 9)];
      }
    },
    clearSearchResults: (state) => {
      state.results = [];
      state.total = 0;
      state.facets = {};
    },
    clearError: (state) => {
      state.error = null;
    },
    /**
     * The live search path is SearchPage's own `runSearch`, not the
     * `performSearch` thunk, so it drives the same loading and error state
     * through these three. `searchFailed` takes the wire code only.
     */
    searchStarted: started,
    searchSucceeded: (state) => {
      state.isLoading = false;
    },
    searchFailed: (state, action: PayloadAction<string | undefined>) => {
      failed(state, action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(performSearch.pending, started)
      .addCase(performSearch.fulfilled, (state, action) => {
        state.isLoading = false;
        state.results = action.payload.results;
        state.total = action.payload.total;
        state.page = action.payload.page;
        state.pageSize = action.payload.pageSize;
        state.facets = action.payload.facets;
      })
      .addCase(performSearch.rejected, (state, action) => {
        failed(state, action.payload?.code);
      });
  },
});

// Actions
export const {
  setQuery,
  addRecentSearch,
  clearSearchResults,
  clearError,
  searchStarted,
  searchSucceeded,
  searchFailed,
} = searchSlice.actions;

// Selectors
export const selectSearchResults = (state: RootState) => state.search.results;
export const selectSearchQuery = (state: RootState) => state.search.query;
export const selectSearchFacets = (state: RootState) => state.search.facets;
export const selectRecentSearches = (state: RootState) => state.search.recentSearches;
export const selectSearchLoading = (state: RootState) => state.search.isLoading;
export const selectSearchError = (state: RootState) => state.search.error;

export default searchSlice.reducer;
